import { StatusDot, type Tone as KitTone } from "@lcc/ui";
import { statusOf, type StatusStyle, type Tone } from "./labels";

/**
 * The module's tones on the Hub's status dots (ADR 0020): red only for SOS and emergencies (`danger`), amber for what
 * needs attention, green for done, grey for the rest (a ring for what is over). The words always sit beside the dot.
 */
const KIT_TONE: Record<Tone, KitTone> = {
  success: "success",
  warning: "warning",
  danger: "emergency",
  neutral: "neutral",
  muted: "ring",
  inverse: "ink",
};

/** A status: a dot and its words (never colour alone). `icon` is accepted for older callers and not drawn. */
export function Badge({ tone, label, title }: { tone: Tone; icon?: string; label: string; title?: string }) {
  return (
    <span className="fer-badge" title={title}>
      <StatusDot tone={KIT_TONE[tone]} label={label} halo={tone === "danger" || tone === "warning" || tone === "success"} />
    </span>
  );
}

export function StatusBadge({ status, title }: { status: StatusStyle | undefined; title?: string }) {
  if (!status) return null;
  return <Badge tone={status.tone} label={status.label} title={title} />;
}

/** A list of server flags as status dots (unknown flags still shown, labelled). */
export function FlagBadges({ map, flags }: { map: Readonly<Record<string, StatusStyle>>; flags: unknown }) {
  const list = Array.isArray(flags) ? flags.filter((f): f is string => typeof f === "string") : [];
  if (list.length === 0) return null;
  return (
    <span className="fer-badges">
      {list.map((f) => {
        const s = statusOf(map, f);
        return <Badge key={f} tone={s.tone} label={s.label} />;
      })}
    </span>
  );
}

/** A yes/no in words beside a dot (never colour alone), e.g. compliant today. */
export function YesNo({ value, yes, no }: { value: boolean | null | undefined; yes: string; no: string }) {
  if (value == null) return <span className="fer-muted">—</span>;
  return value ? <Badge tone="success" label={yes} /> : <Badge tone="warning" label={no} />;
}
